import React, { memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { User, Building2, CheckCircle, X, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { safeArray, safeString } from '@/components/safeRender';
import { formatCurrency } from '@/lib/formatters';
import { effectiveTituloStatus } from '@/lib/utils';
import { calcularDiasAtraso, getGestorActionStatus } from './kanbanCardUtils';

export interface KanbanSortableCardProps {
  card: any;
  isSelected?: boolean;
  onSelect?: (cardId: string, selected: boolean) => void;
  onCardClick?: (card: any) => void;
}

const SortableCardInner: React.FC<KanbanSortableCardProps> = ({
  card,
  isSelected = false,
  onSelect,
  onCardClick,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const cliente = card.cliente;
  const titulos = safeArray(card.titulos);
  const gestorStatus = getGestorActionStatus(card);

  const getStatusLocacao = () => {
    if (!titulos || titulos.length === 0) return null;

    const statusCounts = new Map<string, number>();
    titulos.forEach((titulo: any) => {
      if (titulo.statusLocacao) {
        const normalizedStatus = titulo.statusLocacao === 'APROVADA' ? 'ATIVA' : titulo.statusLocacao;
        statusCounts.set(normalizedStatus, (statusCounts.get(normalizedStatus) || 0) + 1);
      }
    });

    if (statusCounts.size === 0) return null;

    let mostCommonStatus = '';
    let maxCount = 0;
    statusCounts.forEach((count, status) => {
      if (count > maxCount) {
        maxCount = count;
        mostCommonStatus = status;
      }
    });

    return mostCommonStatus;
  };

  const statusLocacao = getStatusLocacao();

  const companyName =
    cliente?.empresa?.nome ??
    cliente?.company?.name ??
    (titulos as Array<{ company?: { name?: string } }>).find((t) => t?.company?.name)?.company?.name ??
    null;

  const titulosVencidos = titulos.filter(
    (t: any) => effectiveTituloStatus(t.status, t.vencimento) === 'VENCIDO' || t.status === 'PARCIAL'
  );
  const titulosParciais = titulos.filter((t: any) => t.status === 'PARCIAL');

  const valorTotal = titulos.reduce((sum: number, t: any) => sum + Number(t.saldo || t.valor), 0);
  const valorVencido = titulosVencidos.reduce((sum: number, t: any) => sum + Number(t.saldo || t.valor), 0);
  const valorParciais = titulosParciais.reduce((sum: number, t: any) => sum + Number(t.saldo || t.valor), 0);
  const valorVencidoTotal = valorVencido + valorParciais;
  const diasAtraso = calcularDiasAtraso(titulos as Array<{ status?: string; vencimento?: string | Date }>);

  const operadoresAlocados = titulos
    .map((t: any) => t.operador)
    .filter((op: any) => op && op.id)
    .reduce((acc: any[], op: any) => {
      if (!acc.find((existing: any) => existing.id === op.id)) {
        acc.push(op);
      }
      return acc;
    }, []);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`
          bg-white dark:bg-slate-800 rounded-lg border shadow-sm
          hover:shadow-md dark:hover:shadow-lg dark:hover:shadow-slate-900/50
          transition-all duration-200
          cursor-grab active:cursor-grabbing
          ${isSelected ? 'ring-2 ring-green-500 border-green-500' : ''}
          ${gestorStatus === 'ACEITO' ? 'border-green-400 dark:border-green-500 hover:border-green-500 dark:hover:border-green-400' : ''}
          ${gestorStatus === 'NEGADO' ? 'border-red-400 dark:border-red-500 hover:border-red-500 dark:hover:border-red-400' : ''}
          ${!gestorStatus && !isSelected ? 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500' : ''}
          ${isDragging ? 'shadow-lg dark:shadow-xl dark:shadow-slate-900/70 rotate-2 scale-105' : ''}
        `}
      onClick={() => onCardClick?.(card)}
    >
      <div className="p-3 border-b border-gray-100">
        {(gestorStatus === 'ACEITO' || gestorStatus === 'NEGADO') && (
          <div className="mb-2 flex justify-center">
            {gestorStatus === 'ACEITO' && (
              <Badge className="text-xs bg-green-100 text-green-800 border-green-200">
                <CheckCircle className="w-3 h-3 mr-1" />
                Aceito pelo Gestor
              </Badge>
            )}
            {gestorStatus === 'NEGADO' && (
              <Badge className="text-xs bg-red-100 text-red-800 border-red-200">
                <X className="w-3 h-3 mr-1" />
                Negado pelo Gestor
              </Badge>
            )}
          </div>
        )}

        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {onSelect && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(card.id, !isSelected);
                }}
                className="cursor-pointer"
              >
                <Checkbox checked={isSelected} onChange={() => {}} />
              </div>
            )}
            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-3 h-3 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-100 truncate">
                {safeString(cliente?.nome, 'Cliente sem nome')}
              </h4>
              {cliente?.codigo && (
                <p className="text-xs text-gray-500 dark:text-slate-400 font-mono">{cliente.codigo}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-1 flex-shrink-0">
            <Badge variant="outline" className="text-xs">
              {titulos.length}
            </Badge>
          </div>
        </div>

        {operadoresAlocados.length > 0 ? (
          <div className="mt-2 flex items-center space-x-2">
            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-3 h-3 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-600 dark:text-slate-300">
                Operador: {operadoresAlocados[0].name}
              </p>
              {operadoresAlocados.length > 1 && (
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  +{operadoresAlocados.length - 1} outros
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-2 flex items-center space-x-2">
            <div className="flex-shrink-0 w-4 h-4 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
              <User className="w-3 h-3 text-gray-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500 dark:text-slate-400">Sem operador alocado</p>
            </div>
          </div>
        )}

        {(companyName || statusLocacao) && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {companyName && (
              <Badge className="text-xs bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600">
                <Building2 className="w-3 h-3 mr-1" />
                <span className="truncate max-w-[180px]">{companyName}</span>
              </Badge>
            )}
            {statusLocacao && (
              <Badge
                className={`
                    text-xs
                    ${statusLocacao === 'ATIVA' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : ''}
                    ${statusLocacao === 'FINALIZADA' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200' : ''}
                    ${statusLocacao === 'CANCELADA' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : ''}
                    ${!['ATIVA', 'FINALIZADA', 'CANCELADA'].includes(statusLocacao) ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : ''}
                  `}
              >
                {statusLocacao}
              </Badge>
            )}
          </div>
        )}
      </div>

      <div className="p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div className="text-center p-1.5 bg-blue-50 rounded-lg border border-blue-100">
            <div className="text-sm font-bold text-blue-700">{titulosVencidos.length + titulosParciais.length}</div>
            <div className="text-xs text-blue-600">Em Atraso</div>
          </div>
          <div className="text-center p-1.5 bg-red-50 rounded-lg border border-red-100">
            <div className="text-sm font-bold text-red-700">{formatCurrency(valorVencidoTotal)}</div>
            <div className="text-xs text-red-600">Valor Vencido</div>
          </div>
        </div>

        {diasAtraso > 0 && (
          <div className="flex items-center justify-between p-1.5 bg-orange-50 rounded-lg border border-orange-100">
            <div className="flex items-center space-x-2">
              <Calendar className="w-3 h-3 text-orange-600" />
              <span className="text-sm font-medium text-orange-800">Dias em Atraso</span>
            </div>
            <span className="text-sm font-bold text-orange-800">
              {diasAtraso} dia{diasAtraso !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        <div className="text-center p-2 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-100">
          <div className="text-sm font-bold text-gray-700 dark:text-slate-200">{formatCurrency(valorTotal)}</div>
          <div className="text-xs text-gray-600 dark:text-slate-300">Total Geral</div>
        </div>
      </div>
    </div>
  );
};

function cardsEqual(prev: KanbanSortableCardProps, next: KanbanSortableCardProps): boolean {
  return (
    prev.card.id === next.card.id &&
    prev.isSelected === next.isSelected &&
    prev.card.gestorActionStatus === next.card.gestorActionStatus &&
    prev.onSelect === next.onSelect &&
    prev.onCardClick === next.onCardClick
  );
}

export const SortableCard = memo(SortableCardInner, cardsEqual);
